import { useMemo, useState, useEffect } from 'react';
import { Box, Paper, Typography, Divider, CircularProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import '../../assets/css/AGGrid-custom.css';
import { CustomTextInput } from '../common/inputFields/customTextInput';
import { CustomButton } from '../common/customButton';
import type { TenantColumnRange } from '../../types/extraction';
import { TENANT_FIELD_CONFIG } from '../../constants/tenantFields';

ModuleRegistry.registerModules([AllCommunityModule]);

interface ExtractionColumnReviewProps {
  tenantColumnRanges: TenantColumnRange[];
  columnOverrides: Record<string, string>;
  onColumnChange: (field: string, value: string) => void;
  onRecalculate?: () => void;
  rowRange?: { start: number; end: number } | null;
  onRowRangeChange?: (field: 'start' | 'end', value: number) => void;
  tenantData: any[];
  isApplyingOverrides?: boolean;
}

const parseExcelRange = (range?: string | null) => {
  if (!range) return null;
  const match = range.match(/^([A-Za-z]+)(\d+):([A-Za-z]+)(\d+)$/);
  if (!match) return null;
  return {
    startColumn: match[1].toUpperCase(),
    startRow: parseInt(match[2], 10),
    endColumn: match[3].toUpperCase(),
    endRow: parseInt(match[4], 10),
  };
};

const ColumnOverrideInput = ({
  params,
  onCommit,
}: {
  params: ICellRendererParams;
  onCommit: (field: string, value: string) => void;
}) => {
  const { data, value } = params;
  const [localValue, setLocalValue] = useState<string>(value ?? '');

  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  return (
    <CustomTextInput
      value={localValue}
      onChange={(newValue) => setLocalValue(String(newValue))}
      heightOverride="25px"
      margin="dense"
      label=""
      onBlur={() => onCommit(data.field_name, localValue)}
    />
  );
};

const ExtractionColumnReview = ({
  tenantColumnRanges = [],
  columnOverrides,
  onColumnChange,
  onRecalculate,
  rowRange,
  onRowRangeChange,
  tenantData = [],
  isApplyingOverrides = false,
}: ExtractionColumnReviewProps) => {
  const previewRows = Array.isArray(tenantData) ? tenantData.slice(0, 10) : [];
  // Use all fields from TENANT_FIELD_CONFIG to ensure all columns are shown
  const previewColumns = useMemo(() => {
    return TENANT_FIELD_CONFIG.map(({ field }) => field as string);
  }, []);

  const parseNumericInput = (val: string | number) => {
    if (typeof val === 'string' && val.trim() === '') {
      return NaN;
    }
    return Number(val);
  };

  const defaultSheetName = useMemo(
    () => tenantColumnRanges.find((range) => !!range.sheet_name)?.sheet_name || '',
    [tenantColumnRanges]
  );

  const columnMappingRowData = useMemo(() => {
    return TENANT_FIELD_CONFIG.map(({ field, label }) => {
      const fieldName = field as string;
      const baseRange = tenantColumnRanges.find((range) => range.field_name === fieldName);
      const parsed = parseExcelRange(baseRange?.cell_range);
      const override = columnOverrides[fieldName] ?? parsed?.startColumn ?? '';
      const sheetName = baseRange?.sheet_name || defaultSheetName;

      return {
        field_name: fieldName,
        fieldLabel: label,
        sheet_name: sheetName,
        cell_range: baseRange?.cell_range || '',
        override,
      };
    });
  }, [tenantColumnRanges, columnOverrides, defaultSheetName]);

  const columnMappingColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Field',
      field: 'fieldLabel',
      flex: 1,
      suppressMovable: true,
    },
    {
      headerName: 'Sheet',
      field: 'sheet_name',
      flex: 1,
      suppressMovable: true,
    },
    {
      headerName: 'AI Column',
      field: 'cell_range',
      flex: 1,
      suppressMovable: true,
    },
    {
      headerName: 'Override',
      field: 'override',
      flex: 1.5,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <ColumnOverrideInput params={params} onCommit={onColumnChange} />
      ),
    },
  ], [onColumnChange]);

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return value.split('T')[0];
    }
    return value;
  };

  const dataPreviewColumnDefs = useMemo<ColDef[]>(() => {
    return previewColumns.map((column) => ({
      headerName: column.replace(/_/g, ' '),
      field: column,
      width: 120,
      suppressMovable: true,
      valueFormatter: (params) => formatValue(params.value),
    }));
  }, [previewColumns]);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4, mt: 3}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6">AI Extraction Review</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review the AI-detected columns and preview the extracted Excel data. Adjust any mappings before creating the deal.
          </Typography>
        </Box>
        {onRecalculate && (
          <CustomButton
            text="Recalculate Preview"
            handleButtonPress={onRecalculate}
            isButtonDisabled={isApplyingOverrides}
            solid
            isLoading={isApplyingOverrides}
            isLoadingText="Recalculating"
          />
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        {onRowRangeChange && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ maxWidth: 200, flex: '1 1 200px' }}>
              <CustomTextInput
              label="Row Start"
              type="number"
              value={rowRange?.start ?? ''}
              onChange={(value) => {
                const numeric = parseNumericInput(value);
                if (!Number.isNaN(numeric)) {
                  onRowRangeChange('start', numeric);
                }
              }}
              heightOverride="36px"
              margin="dense"
            />
            </Box>
            <Box sx={{ maxWidth: 200, flex: '1 1 200px' }}>
              <CustomTextInput
              label="Row End"
              type="number"
              value={rowRange?.end ?? ''}
              onChange={(value) => {
                const numeric = parseNumericInput(value);
                if (!Number.isNaN(numeric)) {
                  onRowRangeChange('end', numeric);
                }
              }}
              heightOverride="36px"
              margin="dense"
            />
            </Box>
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Column Mapping
        </Typography>
        {tenantColumnRanges.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No column metadata available for this extraction.
          </Typography>
        ) : (
          <Box className="ag-theme-alpine custom-ag-grid">
            <AgGridReact              rowData={columnMappingRowData}
              columnDefs={columnMappingColumnDefs}
              domLayout="autoHeight"
              suppressMovableColumns
              suppressCellFocus
              rowHeight={32}
            />
          </Box>
        )}
        {isApplyingOverrides && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Applying overrides…
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Data Preview
        </Typography>
        {previewRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tenant data preview is available for this extraction.
          </Typography>
        ) : (
          <Box className="ag-theme-alpine custom-ag-grid" sx={{ height: 360, width: '100%', overflow: 'auto' }}>
            <AgGridReact              rowData={previewRows}
              columnDefs={dataPreviewColumnDefs}
              suppressMovableColumns
              overlayNoRowsTemplate="No preview data available"
              rowHeight={32}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ExtractionColumnReview;

